import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import type {
  TooltipMetricExplanationProps,
  MetricCategory,
} from './TooltipMetricExplanation.types'

const Container = styled.div`
  font-size: 11px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.primary};
`

const Title = styled.strong`
  font-weight: 600;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.primary};
`

const Section = styled.div`
  margin-top: 10px;

  &:first-child {
    margin-top: 0;
  }
`

const SectionTitle = styled.strong`
  font-weight: 600;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.primary};
  display: block;
  margin-bottom: 3px;
`

const CategoryList = styled.div`
  margin-top: 3px;
`

const CategoryItem = styled.div`
  margin-top: 3px;
  padding-left: 10px;

  &:first-child {
    margin-top: 0;
  }
`

const CategoryLabel = styled.strong`
  font-weight: 600;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.primary};
`

const Warning = styled.div`
  display: block;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
  color: ${({ theme }) => theme.colors.warning};
  font-size: 10px;
  line-height: 1.5;

  &::before {
    content: '⚠️';
    margin-right: 5px;
    font-size: 12px;
  }
`

const Source = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 10px;
  padding: 6px 10px;
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: 5px;
  font-size: 10px;
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.4;

  &::before {
    content: '📊';
    font-size: 10px;
    opacity: 0.8;
  }
`

const renderCategory = (category: MetricCategory, index: number) => {
  const label = category.range
    ? `${category.label} (${category.range})`
    : category.label
  return (
    <CategoryItem key={`${category.label}-${index}`}>
      • <CategoryLabel>{label}:</CategoryLabel> {category.description}
    </CategoryItem>
  )
}

export const TooltipMetricExplanation: React.FC<
  TooltipMetricExplanationProps
> = ({
  title,
  shortDescription,
  calculation,
  categories,
  importance,
  limitations,
  source,
}) => {
  const { t } = useTranslation('ui')

  return (
    <Container>
      <Section>
        <Title>{title}:</Title> {shortDescription}
      </Section>

      {calculation && (
        <Section>
          <SectionTitle>{t('tooltipMetric.howCalculated')}</SectionTitle>
          {calculation}
        </Section>
      )}

      {categories && categories.length > 0 && (
        <Section>
          <SectionTitle>
            {categories[0].range
              ? t('tooltipMetric.categories')
              : t('tooltipMetric.whatItMeans')}
          </SectionTitle>
          <CategoryList>{categories.map(renderCategory)}</CategoryList>
        </Section>
      )}

      {importance && (
        <Section>
          <SectionTitle>{t('tooltipMetric.whyItMatters')}</SectionTitle>
          {importance}
        </Section>
      )}

      {source && <Source>{source}</Source>}

      {limitations && <Warning>{limitations}</Warning>}
    </Container>
  )
}
